/* SIZESOFTX
=========================================================================================
* Descripción: Context del catálogo de Proveedores
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador             | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 14/05/2026 |                           | Creación
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isErrorForm, handleValidarStrings, getDatePost } from '../../settings/utils';
import { defaultTypes } from "../../settings/enums";
import {
    apiProveedoresByNombre,
    apiProveedoresByClave,
    apiProveedoresSave,
    apiProveedoresDelete,
    apiProveedoresFiltro,
} from "../../settings/ApiConfig";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

const getStructProveedor = () => ({
    fecharegistro: getDatePost(),
    claveproveedor: defaultTypes.String,
    nombrecorto: defaultTypes.String,
    razonsocial: defaultTypes.String,
    contacto: defaultTypes.String,
    rfc: defaultTypes.String,
    direccion: defaultTypes.String,
    colonia: defaultTypes.String,
    delegacion: defaultTypes.String,
    ciudad: defaultTypes.String,
    cp: defaultTypes.String,
    telefono1: defaultTypes.String,
    extencion1: defaultTypes.String,
    telefono2: defaultTypes.String,
    extencion2: defaultTypes.String,
    mail: defaultTypes.String,
    condicionespago: defaultTypes.String,
    acuerdos: defaultTypes.String,
});

const getStructProveedorPayload = () => ({
    fecharegistro: { value: getDatePost(), error: false, helper: defaultTypes.String },
    claveproveedor: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    nombrecorto: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    razonsocial: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    contacto: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    rfc: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    direccion: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    colonia: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    delegacion: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    ciudad: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    cp: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    telefono1: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    extencion1: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    telefono2: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    extencion2: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    mail: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    condicionespago: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
    acuerdos: { value: defaultTypes.String, error: false, helper: defaultTypes.String },
});

export const ProveedoresContext = createContext();
export const ProveedoresProvider = (props) => {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const navigation = useNavigate();

    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(getStructProveedor());
    const [proveedorPayLoad, setProveedorPayLoad] = useState(getStructProveedorPayload());
    const [statusEditProveedor, setStatusEditProveedor] = useState(true);
    const [statusEnableFields, setStatusEnableFields] = useState(true);
    const [statusOpenModalBuscarProveedor, setStatusOpenModalBuscarProveedor] = useState(false);
    const [statusOpenModalEliminarProveedor, setStatusOpenModalEliminarProveedor] = useState(false);

    const handleClickRegresarMainDashboard = () => {
        navigation("/*");
    };

    const handleOpenModalBuscarProveedor = () => {
        setStatusOpenModalBuscarProveedor(true);
    };

    const handleCloseModalBuscarProveedor = () => {
        setStatusOpenModalBuscarProveedor(false);
    };

    const handleProveedorSeleccionadoFromModal = (proveedor) => {
        if (proveedor && proveedor.claveproveedor) {
            setProveedorSeleccionado(proveedor);
            const payload = getStructProveedorPayload();
            Object.keys(payload).forEach((key) => {
                if (proveedor[key] !== undefined) {
                    const value = typeof proveedor[key] === 'string' ? proveedor[key].trim() : proveedor[key];
                    payload[key] = { value: value ?? "", error: false, helper: "" };
                }
            });
            setProveedorPayLoad(payload);
            setStatusEditProveedor(true);
            setStatusEnableFields(true);
        }
    };

    const handleChangeDataProveedor = (event, newValue, fieldName) => {
        if (event?.target?.name) {
            const { name, value } = event.target;
            setProveedorSeleccionado(prev => ({ ...prev, [name]: value }));
            return;
        }
        if (newValue !== null && fieldName) {
            const value = typeof newValue === "string" ? newValue : newValue?.[fieldName] || "";
            setProveedorSeleccionado(prev => ({ ...prev, [fieldName]: value }));
        }
    };

    const handleChangeDataProveedorPayload = (e, valor, field) => {
        let name = field ? field : e.target.name ?? "";
        let value = valor !== undefined ? valor : e.target.value ?? "";
        let error = false;
        let helper = "";

        const mensajeError = handleValidarStrings(value, name);
        if (mensajeError) {
            error = true;
            helper = mensajeError;
        }

        const lower = String(value).toLowerCase();
        if (!error && (
            lower.includes("<script") ||
            lower.includes("</script") ||
            lower.includes("javascript:") ||
            lower.includes("onerror=") ||
            lower.includes("onload=")
        )) {
            error = true;
            helper = "Intento de inyección detectado y bloqueado en: (" + name + ")";
        }

        setProveedorPayLoad(prev => {
            const prevValue = prev[name]?.value ?? "";
            const newValueStr = String(value ?? "");
            const prevValueStr = String(prevValue ?? "");
            if (prevValueStr === newValueStr) return prev;
            return {
                ...prev,
                [name]: { ...prev[name], value, error, helper },
            };
        });
    };

    const handleBuscarProveedoresByNombre = async (nombre) => {
        if (!nombre || nombre.trim().length === 0) return;
        const url = apiProveedoresByNombre.replace("{nombre}", nombre.trim());
        const result = await GetRequest({
            url,
            loader: "Buscando proveedores por nombre...",
        });

        if (result.hasError) {
            handleOpenAlert("No se encontraron proveedores con ese nombre");
            return [];
        }
        return result?.result ?? [];
    };

    const handleBuscarProveedoresFiltro = async (filtro, pageIndex = 0) => {
        const inicial = pageIndex * 15;
        const totalPorPagina = 15;

        let url = apiProveedoresFiltro
            .replace("{busqueda}", encodeURIComponent(filtro))
            .replace("{registroinicial}", inicial)
            .replace("{totalregistros}", totalPorPagina);

        const result = await GetRequest({
            url,
            loader: "Buscando proveedores...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al buscar proveedores: " + result.message);
            return { lista: [], total: 0 };
        }

        const proveedores = result.result?.listaproveedores ?? [];
        const total = result.result?.totalregistros ?? 0;

        return { lista: proveedores, total };
    };

    const handleCargarProveedorByClave = async (clave) => {
        if (!clave || clave.trim().length === 0) return;
        const url = apiProveedoresByClave.replace("{clave}", clave.trim().toUpperCase());
        const result = await GetRequest({
            url,
            loader: "Cargando datos del proveedor...",
        });

        if (result.hasError) {
            handleOpenAlert("No se encontró el proveedor con la clave indicada");
            return;
        }

        setProveedorSeleccionado(result?.result);
    };

    const handleClickNuevoProveedor = () => {
        handleClickDeshacer();
        setStatusEditProveedor(false);
        setStatusEnableFields(false);
    };

    const handleClickEditarProveedor = () => {
        setStatusEditProveedor(false);
        setStatusEnableFields(false);
    };

    const handleClickDeshacer = () => {
        setProveedorSeleccionado(getStructProveedor());
        setProveedorPayLoad(getStructProveedorPayload());
        setStatusEditProveedor(true);
        setStatusEnableFields(true);
    };

    const handleClickGuardarProveedor = async () => {
        const resultPayload = {};
        const upperIfString = (v, key) => {
            if (typeof v !== "string") return v;
            const noUpperCaseFields = ['mail', 'email'];
            if (noUpperCaseFields.includes(key.toLowerCase())) {
                return v.toLowerCase();
            }
            return v.toUpperCase();
        };

        Object.entries(proveedorPayLoad).forEach(([key, meta]) => {
            if (meta && typeof meta === "object" && "value" in meta) {
                resultPayload[key] = upperIfString(meta.value, key);
            } else {
                resultPayload[key] = upperIfString(meta, key);
            }
        });

        const result = await PostRequest({
            url: apiProveedoresSave,
            body: resultPayload,
            loader: "Guardando datos del proveedor...",
        });

        if (result.hasError || result.errors) {
            handleOpenAlert("Error al guardar datos del proveedor: " + result.message);
            return;
        }

        handleOpenAlert("Proveedor guardado correctamente", "ok");
        setStatusEditProveedor(true);
        setStatusEnableFields(true);

        if (result.result && result.result.claveproveedor) {
            setProveedorSeleccionado(result.result);
            const payload = getStructProveedorPayload();
            Object.keys(payload).forEach((key) => {
                if (result.result[key] !== undefined) {
                    payload[key] = { value: result.result[key] ?? "", error: false, helper: "" };
                }
            });
            setProveedorPayLoad(payload);
        } else {
            setProveedorSeleccionado(resultPayload);
        }
    };

    const handleClickEliminarProveedor = async () => {
        if (!proveedorSeleccionado.razonsocial || proveedorSeleccionado.razonsocial.trim().length === 0) return;

        const url = apiProveedoresDelete.replace("{razonsocial}", encodeURIComponent(proveedorSeleccionado.razonsocial.trim()));

        const result = await PostRequest({
            url,
            loader: "Eliminando proveedor del sistema...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al eliminar el proveedor: " + result.message);
            return;
        }

        handleOpenAlert("Proveedor eliminado correctamente", "ok");
        setStatusOpenModalEliminarProveedor(false);
        handleClickDeshacer();
    };

    const handleClickRefrescarDatos = async () => {
        handleOpenAlert("Datos actualizados correctamente", "ok");
    };

    return (
        <ProveedoresContext.Provider
            value={{
                proveedorSeleccionado, setProveedorSeleccionado,
                proveedorPayLoad, setProveedorPayLoad, getStructProveedorPayload,
                statusEditProveedor, setStatusEditProveedor,
                statusEnableFields,
                statusOpenModalEliminarProveedor, setStatusOpenModalEliminarProveedor,
                statusOpenModalBuscarProveedor, setStatusOpenModalBuscarProveedor,
                handleClickRegresarMainDashboard,
                handleOpenModalBuscarProveedor,
                handleCloseModalBuscarProveedor,
                handleProveedorSeleccionadoFromModal,
                handleChangeDataProveedor,
                handleChangeDataProveedorPayload,
                handleBuscarProveedoresByNombre,
                handleBuscarProveedoresFiltro,
                handleCargarProveedorByClave,
                handleClickNuevoProveedor,
                handleClickEditarProveedor,
                handleClickDeshacer,
                handleClickGuardarProveedor,
                handleClickEliminarProveedor,
                handleClickRefrescarDatos,
            }}
        >
            {props.children}
        </ProveedoresContext.Provider>
    );
};

export const useProveedores = () => useContext(ProveedoresContext);